import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileSignature } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { Badge, Button, Card, Spinner } from '@/components/ui';
import { listMyLetters, type InboxEntry } from '@/api/services/hrDocuments.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { formatLetterDate, LETTER_STATUS } from './letterFormat';

function LetterRow({ letter }: { letter: InboxEntry }): React.ReactElement {
  const navigate = useNavigate();
  const status = LETTER_STATUS[letter.status];
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {letter.title}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-300">
          {letter.awaitingMySignature && letter.expiresAt
            ? `Sign by ${formatLetterDate(letter.expiresAt)}`
            : letter.completedAt
              ? `Signed ${formatLetterDate(letter.completedAt)}`
              : letter.issuedAt
                ? `Issued ${formatLetterDate(letter.issuedAt)}`
                : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={status.variant}>{status.label}</Badge>
        <Button
          size="sm"
          variant={letter.awaitingMySignature ? 'primary' : 'secondary'}
          onClick={() => navigate(`/my-letters/${letter._id}`)}
        >
          {letter.awaitingMySignature ? 'Review and sign' : 'Open'}{' '}
          <span className="sr-only">{letter.title}</span>
        </Button>
      </div>
    </li>
  );
}

/**
 * The signed-in user's HR letters: those to sign first, then everything they
 * have been issued or have signed.
 */
export function MyLettersPage(): React.ReactElement {
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-letters'],
    queryFn: listMyLetters,
  });
  const letters = Array.isArray(data) ? data : [];
  const toSign = letters.filter((letter) => letter.awaitingMySignature);
  const others = letters.filter((letter) => !letter.awaitingMySignature);

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Card>
        <div className="flex justify-center py-12" role="status">
          <Spinner />
          <span className="sr-only">Loading letters</span>
        </div>
      </Card>
    );
  } else if (error) {
    body = (
      <Card>
        <p className="text-sm text-danger" role="alert">
          {getErrorMessage(error)}
        </p>
      </Card>
    );
  } else if (letters.length === 0) {
    body = (
      <Card>
        <div className="py-10 text-center">
          <FileSignature className="mx-auto h-8 w-8 text-gray-400" aria-hidden />
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
            No letters yet. Letters HR issues to you appear here.
          </p>
        </div>
      </Card>
    );
  } else {
    body = (
      <div className="space-y-6">
        {toSign.length > 0 && (
          <Card
            header={
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Waiting for your signature
              </h2>
            }
            contentClassName="p-0"
          >
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {toSign.map((letter) => (
                <LetterRow key={letter._id} letter={letter} />
              ))}
            </ul>
          </Card>
        )}
        {others.length > 0 && (
          <Card
            header={
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                All letters
              </h2>
            }
            contentClassName="p-0"
          >
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {others.map((letter) => (
                <LetterRow key={letter._id} letter={letter} />
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Self Service', to: '/dashboard' }, { label: 'My Letters' }]}
      icon={FileSignature}
      title="My Letters"
      description="Letters HR has issued to you, and those waiting for your signature."
      className="gap-0"
      hideHeader
    >
      <div className="mx-auto w-full max-w-4xl">{body}</div>
    </PageShell>
  );
}
